import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { DateTimeService, NavigationService } from 'i2g-ng-utils';
import { List, Map } from 'immutable';
import { isUndefined, noop, snakeCase } from 'lodash';
import { merge, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  finalize,
  map,
  skip,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';
import { v4 } from 'uuid';
import { Account } from '../../../account-data/account.model';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import {
  DocumentTypesEnum,
  IEditTaxRateProperties,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { Discount } from '../../../document-data/sub-models/discount.model';
import { Schedule } from '../../../document-data/sub-models/schedule.model';
import { Shipping } from '../../../document-data/sub-models/shipping.model';
import { States } from '../../../document-data/sub-models/states.model';
import { TaxTotal } from '../../../document-data/sub-models/tax-total.model';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import {
  DatePickerHorizontalAlignmentsEnum,
  IDatePickerFieldOptions,
} from '../../../form-master/components/date-picker-field/date-picker-field.component';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { SettingsDataDispatcher } from '../../../settings-data/settings-data.dispatcher';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { DropdownHorizontalAlignmentsEnum } from '../../../shared/components/dropdown/dropdown.component';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { LoggerService } from '../../../shared/services/logger/logger.service';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import {
  DIALOG_TYPE as discountDialog,
  IDocumentDiscountDialogData,
} from '../document-discount-dialog/document-discount-dialog.component';
import {
  DIALOG_TYPE as editTaxDialog,
  IEditTaxDialogData,
} from '../edit-tax-dialog/edit-tax-dialog.component';
import { DIALOG_TYPE as shippingInfoDialog } from '../shipping-info-dialog/shipping-info-dialog.component';
import { AsideCreateBuilder } from './aside-create.builder';
import { SETTINGS_MASTER_ROUTE } from '../../../main/main.data';
import { SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE } from '../../../settings/settings.data';

@Component({
  selector: 'app-aside-create',
  templateUrl: './aside-create.component.html',
  styleUrls: ['./aside-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [AsideCreateBuilder],
})
export class AsideCreateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() documentNumber: string;
  @Input() documentShipping: Shipping;
  @Input() documentDiscount: Discount;
  @Input() documentCalculationResult: CalculationResult;
  @Input() documentTaxes: List<Tax>;
  @Input() documentState: States;
  @Input() documentDueDate: string;
  @Input() paymentsSettings: PaymentsSettings;
  @Input() documentType: DocumentTypesEnum;
  @Input() documentWithholdingTax: WithholdingTax;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() documentSchedule: Schedule;
  @Input() documentDateTerms: { docDate: string; terms: number };
  @Input() showDueDate: boolean;
  @Input() showPaid: boolean;
  @Input() useOutstandingBalance: boolean;
  @Input() showTotal: boolean;
  @Input() enableDiscount: boolean;
  @Input() enableShipping: boolean;
  @Input() enableDeposits: boolean;
  @Input() addDepositForFuture: DepositToggleValue;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() featureToggles: Toggles;
  @Input() documentFeatureName: string;
  @Input() account: Account;
  @Input() deposit: Deposit;
  @Input() document: Document;
  @Input() depositRequestFeatureName: string;

  @Output() onUpdateAsideCreate: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output() onUpdateSettingsContent: EventEmitter<
    Map<string, any>
  > = new EventEmitter<Map<string, any>>();
  @Output()
  onNavigateToConvertedDoc: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onEditTaxRates: EventEmitter<IEditTaxRateProperties> = new EventEmitter<IEditTaxRateProperties>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  datePickerHorizontalAlignment: DatePickerHorizontalAlignmentsEnum;
  discountDialogOpened: boolean;
  documentNumberChangeDialogId: string;
  dueDateOptions: IDatePickerFieldOptions;
  formKeys: any;
  form: FormGroup;
  selectOptions: List<ISelectOption>;
  shippingInfoDialogOpened: boolean;
  taxSummary: List<{ info: Tax; taxTotals: List<TaxTotal> }>;
  termsHorizontalAlignment: DropdownHorizontalAlignmentsEnum;
  currencyCode: string;
  locale: string;
  editDiscountAction: ITrackingAction;
  editShippingAction: ITrackingAction;
  termsTappedAction: ITrackingAction;
  termsSelectedAction: ITrackingAction;
  editTaxAction: ITrackingAction;
  enableRecurring$: Observable<boolean>;
  DocumentTypesEnum: typeof DocumentTypesEnum;
  documentAllAccess: boolean;
  depositRequestAllAccess: boolean;
  depositRequestReadAccess: boolean;
  isEstimate: boolean;
  underwritingUrl$: Observable<string>;

  constructor(
    private _asideCreateBuilder: AsideCreateBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _dialogService: DialogService,
    private _loggerService: LoggerService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _dateTimeService: DateTimeService,
    private _featuresDataStore: FeaturesDataStore,
    private _settingsDataStore: SettingsDataStore,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _trackingDispatcher: TrackingDispatcher,
    private navigationService: NavigationService,
    private _globalBannerService: GlobalBannerService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const documentCalculationResultChanges: SimpleChange =
      changes.documentCalculationResult;
    const documentTaxesChanges: SimpleChange = changes.documentTaxes;
    const documentNumberChanges: SimpleChange = changes.documentNumber;
    const documentDateTermsChanges: SimpleChange = changes.documentDateTerms;
    const documentSettingsChanges: SimpleChange = changes.documentSettings;

    if (
      (changes.documentType || changes.featureToggles) &&
      this.documentType &&
      this.featureToggles
    ) {
      let isEstimate = false;
      let featureName: string;

      switch (this.documentType) {
        case DocumentTypesEnum.invoice:
          featureName = Toggles.KEYS.INVOICES;
          break;
        case DocumentTypesEnum.estimate:
          featureName = Toggles.KEYS.ESTIMATE;
          isEstimate = true;
          break;
        case DocumentTypesEnum.credit_memo:
          featureName = Toggles.KEYS.CREDIT_MEMO;
          break;
        case DocumentTypesEnum.purchase_order:
          featureName = Toggles.KEYS.PURCHASE_ORDER;
          break;
      }

      this.isEstimate = isEstimate;
      this.documentFeatureName = featureName;
      this.documentAllAccess = this.featureToggles.isAllAccess(featureName);
    }

    if (
      (changes.featureToggles || changes.depositRequestFeatureName) &&
      this.featureToggles &&
      this.depositRequestFeatureName
    ) {
      this.depositRequestAllAccess = this.featureToggles.isAllAccess(
        this.depositRequestFeatureName
      );
      this.depositRequestReadAccess = this.featureToggles.isReadOnlyAccess(
        this.depositRequestFeatureName
      );
    }

    if (documentSettingsChanges && this.documentSettings) {
      this.currencyCode = this.documentSettings.currencyCode;
      this.locale = this.documentSettings.locale;
    }

    if (
      (documentCalculationResultChanges || documentTaxesChanges) &&
      this.documentCalculationResult
    ) {
      if (this.documentTaxes) {
        this.taxSummary = this._asideCreateBuilder.buildTaxSummary(
          this.documentTaxes,
          this.documentCalculationResult.taxes
        );
      }
    }

    if (
      documentDateTermsChanges &&
      this.documentDateTerms &&
      this.showDueDate
    ) {
      this.dueDateOptions = this._asideCreateBuilder.buildDatePickerOptions(
        this.documentDateTerms.docDate
      );
    }

    if (this.form) {
      if (documentNumberChanges) {
        this.form
          .get(this.formKeys.DOCUMENT_NUMBER)
          .setValue(documentNumberChanges.currentValue, { emitEvent: false });
      }

      if (documentDateTermsChanges) {
        if (this.documentDateTerms) {
          const { docDate, terms } = this.documentDateTerms;

          this.form.patchValue({
            [this.formKeys.DATE]: docDate,
            [this.formKeys.TERMS]: terms,
            [this.formKeys.DUE_DATE]: this._asideCreateBuilder.buildDueDate(
              docDate,
              terms
            ),
          });
        }
      }
    } else if (documentDateTermsChanges) {
      const newDocumentDate = this.documentDateTerms
        ? this.documentDateTerms.docDate
        : '';
      const newDocumentTerms = this.documentDateTerms
        ? this.documentDateTerms.terms
        : void 0;

      if (newDocumentDate && !isUndefined(newDocumentTerms)) {
        this.formKeys = this._asideCreateBuilder.FORM_KEYS;
        this.form = this._asideCreateBuilder.buildForm(
          newDocumentDate,
          newDocumentTerms,
          this.documentNumber
        );
        this.selectOptions = this._asideCreateBuilder.buildTermsOptions(
          this.form.get(this.formKeys.TERMS).value
        );

        const dateControl: AbstractControl = this.form.get(this.formKeys.DATE);
        const termsControl: AbstractControl = this.form.get(
          this.formKeys.TERMS
        );

        merge(dateControl.valueChanges, termsControl.valueChanges)
          .pipe(
            startWith([dateControl.value, termsControl.value]),
            skip(1),
            map(() => [dateControl.value, parseInt(termsControl.value, 10)]),
            takeUntil(this._destroyed)
          )
          .subscribe(([docDate, terms]: any[]) =>
            this.onUpdateAsideCreate.emit(
              this._asideCreateBuilder.buildDocumentPropsFromDateTerms(
                docDate,
                terms,
                this.showDueDate
              )
            )
          );

        this.form
          .get(this.formKeys.DUE_DATE)
          .valueChanges.pipe(
            distinctUntilChanged((a, b) =>
              this._dateTimeService.isSameDate(a, b)
            ),
            takeUntil(this._destroyed)
          )
          .subscribe((dueDate: string) => {
            const docDate = this._dateTimeService.getDateString(
              this.form.get(this.formKeys.DATE).value
            );
            const newDueDate = this._dateTimeService.getDateString(dueDate);
            const terms = Math.min(
              this._dateTimeService.calculateNumberOfUnitsFromDate(
                newDueDate,
                docDate,
                'day',
                'days'
              ),
              999
            );

            this.selectOptions = this._asideCreateBuilder.buildTermsOptions(
              terms
            );

            this._changeDetectorRef.markForCheck();

            if (this.trackingObject && this.trackingScreenName) {
              const identifier = `${snakeCase(
                this.documentType
              )}_due_date`.toLowerCase();
              const dueDateAction = this._trackingActionBuilder.buildButtonTapped(
                identifier,
                this.trackingScreenName
              );
              const trackingAdditionalProps = {
                number_of_days: terms,
              };

              this._trackingDispatcher.trackEvent(
                this.trackingObject,
                dueDateAction,
                trackingAdditionalProps
              );
            }

            setTimeout(() =>
              this.onUpdateAsideCreate.emit(
                this._asideCreateBuilder.buildDocumentPropsFromDateTerms(
                  docDate,
                  terms,
                  this.showDueDate
                )
              )
            );
          });
      }
    }
  }

  ngOnInit() {
    this.documentNumberChangeDialogId = v4();
    this.datePickerHorizontalAlignment =
      DatePickerHorizontalAlignmentsEnum.right;
    this.termsHorizontalAlignment = DropdownHorizontalAlignmentsEnum.right;
    this.enableRecurring$ = this._featuresDataStore.isAllAccess(
      Toggles.KEYS.SCHEDULE
    );
    this.DocumentTypesEnum = DocumentTypesEnum;

    if (this.trackingObject && this.trackingScreenName) {
      this.editDiscountAction = this._trackingActionBuilder.buildButtonTapped(
        'discount',
        this.trackingScreenName
      );
      this.editShippingAction = this._trackingActionBuilder.buildButtonTapped(
        'shipping_info',
        this.trackingScreenName
      );
      this.termsTappedAction = this._trackingActionBuilder.buildDropdownTapped(
        'terms',
        this.trackingScreenName
      );
      this.termsSelectedAction = this._trackingActionBuilder.buildDropdownSelected(
        'terms',
        this.trackingScreenName
      );
      this.editTaxAction = this._trackingActionBuilder.buildButtonTapped(
        'tax_item',
        this.trackingScreenName
      );
    }
    this.underwritingUrl$ = this._settingsDataStore.getUnderwritingUrl();
  }

  updateDiscount(discountProps: Map<string, any>) {
    this._loggerService.log('update document discount');

    this.onUpdateAsideCreate.emit(
      this._asideCreateBuilder.buildDocumentPropsFromDiscount(discountProps)
    );
  }

  updateTax(tax: Tax) {
    this._loggerService.log('update document tax');

    this.onEditTaxRates.emit(
      this._asideCreateBuilder.buildDocumentPropsFromTax(
        this.documentTaxes,
        tax
      )
    );
  }

  updateDocumentNumber(docNumber: string) {
    this.onUpdateAsideCreate.emit(
      this._asideCreateBuilder.buildDocumentPropsFromDocNumber(docNumber)
    );
  }

  showShippingInfoDialog() {
    this._dialogService.openDialog({ type: shippingInfoDialog });

    this._dialogService.dialogTriggers$
      .pipe(
        filter(({ type, action }: IDialogTrigger) => {
          return (
            type === shippingInfoDialog && action === DialogActionsEnum.close
          );
        }),
        take(1)
      )
      .subscribe(() => {
        this.shippingInfoDialogOpened = false;

        this._changeDetectorRef.markForCheck();
      });

    this.shippingInfoDialogOpened = true;
  }

  showDiscountDialog() {
    const data: IDocumentDiscountDialogData = {
      discount: this.documentDiscount,
      currencyCode: this.documentSettings
        ? this.documentSettings.currencyCode
        : null,
      locale: this.documentSettings ? this.documentSettings.locale : null,
      trackingDialogName: TrackingDialogNamesEnum.discount,
      trackingScreenName: this.trackingScreenName,
    };

    this._dialogService.openDialog({
      type: discountDialog,
      data: data,
    });

    this._dialogService.dialogTriggers$
      .pipe(
        filter(({ type, action }: IDialogTrigger) => {
          return type === discountDialog && action === DialogActionsEnum.close;
        }),
        take(1)
      )
      .subscribe(() => {
        this.discountDialogOpened = false;

        this._changeDetectorRef.markForCheck();
      });

    this.discountDialogOpened = true;
  }

  showEditTaxDialog(tax: Tax) {
    const data: IEditTaxDialogData = {
      tax: tax,
      allTaxes: this.documentTaxes,
      locale: this.documentSettings ? this.documentSettings.locale : null,
    };

    this._dialogService.openDialog({
      type: editTaxDialog,
      data: data,
    });
  }

  showDocumentNumberChangeConfirmDialog() {
    const documentNumberControl = this.form.get(this.formKeys.DOCUMENT_NUMBER);

    if (documentNumberControl.invalid) {
      documentNumberControl.reset(this.documentNumber, { emitEvent: false });
    } else if (documentNumberControl.dirty) {
      this._dialogService.openConfirmDialog(this.documentNumberChangeDialogId);

      documentNumberControl.markAsPristine();
    }
  }

  onDocumentNumberChangeConfirmClose({ result }: IConfirmDialogResult) {
    const documentNumberControl = this.form.get(this.formKeys.DOCUMENT_NUMBER);

    if (result) {
      this.updateDocumentNumber(documentNumberControl.value);
    } else {
      documentNumberControl.reset(this.documentNumber, { emitEvent: false });
    }
  }

  removeDepositRequest() {
    this.onUpdateAsideCreate.emit(
      this._asideCreateBuilder.buildDocumentPropsFromDeposit(null)
    );
  }

  saveDepositRequest(deposit: Deposit) {
    this.onUpdateAsideCreate.emit(
      this._asideCreateBuilder.buildDocumentPropsFromDeposit(deposit)
    );
  }

  onDepositDisabledDialogClose({ result }: IConfirmDialogResult) {
    if (result) {
      this.onNavigateToConvertedDoc.emit();
    }
  }

  onDepositDialogPreventOpen() {
    if (!this.documentAllAccess) {
      this._dialogService.openGrowthDialog(
        BannerCanvasTypesEnum.feature_lock,
        this.documentFeatureName
      );
    }
  }

  /**
   * Instead of enabling payments we redirect users to PayPal,
   *
   * since: new paypal onboarding flow
   */
  turnOnPayments() {
    this.navigationService.goTo([
      SETTINGS_MASTER_ROUTE,
      SETTINGS_CLIENT_PAYMENT_OPTIONS_ROUTE,
    ]);
  }

  private _enableCardPayment() {
    this._settingsDataDispatcher
      .updateCardPaymentsSettings(
        true,
        this.navigationService.getLocationHref()
      )
      .pipe(
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(noop, (error) =>
        this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  private _enablePaypal() {
    this._settingsDataDispatcher
      .updatePaypalSettings(true)
      .pipe(
        take(1),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(noop, noop);
  }

  ngOnDestroy() {
    this._dialogService.closeDialog({ type: shippingInfoDialog });
    this._dialogService.closeDialog({ type: discountDialog });
    this._dialogService.closeDialog({ type: editTaxDialog });
    this._dialogService.closeConfirmDialog(this.documentNumberChangeDialogId);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
